import React from 'react';
import { Col, Content, Div, Label, Row, Spacer, Button, Colors, Chip } from '@gower/react-ui/components';
import { useStore } from '../store';
import { MdAddCircleOutline, MdFileDownload } from '@gower/react-ui/icons';
import { DataGrid, DataGridColumn, DataGridTemplateProps } from '@gower/react-ui/template/data-grid';
import { User } from '@gower/shared-sdk/core/users';
import { RoutesEnum } from 'app/routes';
import { WaitAtLeast } from '@gower/utils';
import { adminOnly, employeeOnly } from '../../../app/credential-provider';

export const UsersDesktop = () => {

    const store = useStore();

    return <>

        <Content columns>
            <Col>
                <Row padding={15}>
                    <Col gutter center>
                        <Div padding={10} >
                            <Label xxsemi p bold marginBottom={5}>Usuarios</Label>
                            <Label xsmall p textGrey>Operadores y usuarios del sistema.</Label>
                        </Div>
                    </Col>
                    <Spacer />
                    <Col gutter center show={adminOnly()}>
                        <Button
                            actionTheme
                            icon={<MdFileDownload size={16} color={Colors['surface-6']} />}
                            label="Exportar"
                            disabled={!store.Status.isOk}
                            loading={store.DownloadStatus.isLoading}
                            onClick={() => store.downloadList()}
                        />
                    </Col>
                    <Col gutter center>
                        <CreateMenu />
                    </Col>
                </Row>
            </Col>
            <Spacer vertical noScroll>
                <DataGrid
                    loader
                    store={store}
                    autoSorting
                    showFilters={employeeOnly() ? ["status"] : ["status", "group"]}
                    showReload
                    scroll
                    showPagination
                    showSearch
                    mobileTemplate={( { data } ) => (
                        <Row white center borderRadius={5} minHeight={45} padding={10} activeEffect>
                            <Col cols={6}>
                                <Label label={data.Id} />
                            </Col>
                            <Col cols={6}>
                                <Label label={data.CompleteName} />
                            </Col>
                        </Row>
                    )}
                >

                    <DataGridColumn field="id" title="Usuario"
                        template={( { data }: DataGridTemplateProps<User> ) => (
                            <Div block>
                                <Label p label={data.Nickname} />
                            </Div>
                        )}
                    />

                    <DataGridColumn field="id" title="Nombre"
                        template={( { data }: DataGridTemplateProps<User> ) => (
                            <Div block>
                                <Label p label={data.CompleteName} />
                            </Div>
                        )}
                    />

                    <DataGridColumn field="email" title="Email"
                        template={( { data }: DataGridTemplateProps<User> ) => (
                            <Div block>
                                <Label p label={data.Email} />
                            </Div>
                        )}
                    />

                    <DataGridColumn field="licenses" title="Matrículas y Convenios"
                        template={( { data }: DataGridTemplateProps<User> ) => (
                            <Div block>
                                <Label p label={[
                                    ...( Array.isArray( data.Metadata.licenses ) ? data.Metadata.licenses?.map( ( item ) => (
                                        <Chip padding={3} xsmall uppercase label={item} margin={4} />
                                    ) ) : [] ),
                                    ...( Array.isArray( data.Metadata.agreements ) ? data.Metadata.agreements?.map( ( item ) => (
                                        <Chip padding={3} xsmall uppercase label={item} margin={4} />
                                    ) ) : [] )
                                ] as any} />
                            </Div>
                        )}
                    />

                    {/* <DataGridColumn field="status" title="Estado" size={80}
                        template={( { data }: DataGridTemplateProps<User> ) => (
                            <Row justifyCenter >
                                <Chip
                                    xsmall
                                    textCenter
                                    red={data.Status.isBloqued}
                                    green={data.Status.isActive}
                                >
                                    {data.Status.Description}
                                </Chip>
                            </Row>
                        )}
                    /> */}

                    <DataGridColumn field="group" title="Grupo"
                        size={80}
                        template={( { data }: DataGridTemplateProps<User> ) => (
                            <Div block textCenter>
                                <Label p label={data.Group.Description} />
                            </Div>
                        )}
                    />

                    <DataGridColumn
                        field="actions"
                        title=""
                        size={130}
                        template={( { data }: DataGridTemplateProps<User> ) => (
                            <Actions data={data} />
                        )}
                    />
                </DataGrid>
            </Spacer>
        </Content>
    </>;
};


const Actions = ( { data } ) => {

    const store = useStore();
    const [loading, setLoading] = React.useState<'none' | 'active' | 'disable'>( 'none' );

    async function active () {
        setLoading( 'active' );
        const wait = WaitAtLeast( 500 );
        await store.active( data );
        await WaitAtLeast( wait );
        setLoading( 'none' );
    }

    async function disable () {
        setLoading( 'disable' );
        const wait = WaitAtLeast( 500 );
        await store.disable( data );
        await WaitAtLeast( wait );
        setLoading( 'none' );
    }

    return <>
        <Row end noWrap justifyEnd>
            <Col gutter center>
                <Button
                    disabled={loading !== 'none' || !checkUser(data)}
                    minHeight={28}
                    height={28}
                    depressed
                    green={data.Status.isActive}
                    textWhite={data.Status.isActive}
                    hoverTextColor={data.Status.isActive ? "white" : undefined}
                    xsmall
                    label="Activo"
                    padding={4}
                    borderRadiusRight={0}
                    loading={loading === 'active'}
                    onClick={() => active()}
                />
                <Button
                    minHeight={28}
                    height={28}
                    depressed
                    red={data.Status.isBloqued}
                    textWhite={data.Status.isBloqued}
                    hoverTextColor={data.Status.isBloqued ? "white" : undefined}
                    xsmall
                    label="Desactivado"
                    padding={4}
                    borderRadiusLeft={0}
                    disabled={loading !== 'none' || !checkUser(data)}
                    loading={loading === 'disable'}
                    onClick={() => disable()}
                />
            </Col>
            <Col>
                <Button
                    block
                    bold
                    xxsmall
                    label="Editar"
                    disabled={loading !== 'none' || !checkUser(data)}
                    reactLink={checkUser(data) && `${ RoutesEnum.UsersEdit }/${ data.Id }`}
                />
            </Col>
        </Row>
    </>;
};

const CreateMenu: React.FC = ( { } ) => {

    const menuEl = React.useRef( null );

    function closeMenu () {
        menuEl.current?.hide();
        return true;
    }

    return <>

        <Button
            primary
            icon={<MdAddCircleOutline size={16} />}
            label="Crear nuevo"
            reactLink={RoutesEnum.UsersCreate}
        />

        {/* <Button
            primary
            icon={<MdAddCircleOutline size={16} />}
            label="Crear nuevo"
            onClick={( event ) => menuEl.current.toggle( event )}
            contentRight={<TbChevronDown size={16} />}
        />
        <Panel dismissable ref={menuEl}>
            <Row width={200} noWrap columns onClick={closeMenu}>
                <Button xxsmall noneTheme justify="flex-start" width={200} flat borderRadius={0} label="Crear nuevo usuario" reactLink={RoutesEnum.UsersCreate} />
                 <Button xxsmall noneTheme justify="flex-start" width={200} flat borderRadius={0} label="Importar desde archivo" reactLink={RoutesEnum.UsersImport} /> 
            </Row>
        </Panel> */}
    </>;
};

function checkUser ( user: User ) {
    
    if ( employeeOnly() ) {
        return user.Group.isEmployee;
    }

    return adminOnly();

}